<template>
  <div :class="['d-flex align-center', { 'download-excel--compact': compact }]" :data-testid="`${testIdPrefix}__container`">
    <v-tooltip bottom :disabled="!isGenerateDisabled" content-class="tooltip-bottom">
      <template v-slot:activator="{ on, attrs }">
        <span v-bind="attrs" v-on="on" class="d-inline-block">
          <v-btn
            :data-testid="`${testIdPrefix}__button`"
            :color="buttonColor"
            class="shadow-off rounded-r-0"
            :disabled="isGenerateDisabled"
            @click="$emit('generate')"
          >
            <v-icon :data-testid="`${testIdPrefix}__icon`" left small>mdi-file-excel</v-icon>
            <span>{{ buttonText }}</span>
          </v-btn>
        </span>
      </template>
      <span>{{ disabledTooltipText }}</span>
    </v-tooltip>
    <v-menu
      :data-testid="`${testIdPrefix}__menu`"
      offset-y
      left
      v-model="menuOpen"
      :close-on-content-click="false"
      transition="slide-y-transition"
      min-width="400"
      max-width="400"
      eager
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          :data-testid="`${testIdPrefix}__chevron-btn`"
          v-bind="attrs"
          v-on="on"
          :color="buttonColor"
          class="shadow-off rounded-l-0 px-1"
          min-width="32"
        >
          <v-icon :data-testid="`${testIdPrefix}__chevron`" small>mdi-chevron-down</v-icon>
        </v-btn>
      </template>
      <v-card :data-testid="`${testIdPrefix}__menu-card`" class="elevation-8 rounded-lg download-menu-card">
        <div :data-testid="`${testIdPrefix}__menu-header`" :class="['primary', compact ? 'pa-2' : 'pa-3']">
          <div class="d-flex align-center">
            <span :data-testid="`${testIdPrefix}__menu-header-title`" :class="['font-weight-bold', compact ? 'caption' : '']"  class="white--text">Recent Downloads</span>
            <v-spacer></v-spacer>
            <v-tooltip bottom content-class="tooltip-bottom">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  :data-testid="`${testIdPrefix}__refresh-button`"
                  v-bind="attrs"
                  v-on="on"
                  icon
                  @click.stop="handleRefresh"
                  class="shadow-off white--text"
                >
                  <v-icon :data-testid="`${testIdPrefix}__refresh-icon`" size="24">mdi-refresh</v-icon>
                </v-btn>
              </template>
              <span>Refresh</span>
            </v-tooltip>
          </div>
        </div>
        <v-divider></v-divider>
        <v-list :data-testid="`${testIdPrefix}__list`" dense class="py-1" style="max-height: 250px; overflow-y: auto;">
          <div v-if="loadingHistory" class="pa-3 text-center">
            <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
            <div class="caption mt-2">Loading files...</div>
          </div>
          <div v-else-if="downloadHistory.length === 0" class="pa-3 text-center grey--text">
            <v-icon color="grey" class="mb-1">mdi-file-document-outline</v-icon>
            <div class="caption">No files available</div>
        </div>
          <v-list-item
            v-else
            v-for="(file, index) in downloadHistory"
            :key="index"
            :data-testid="`${testIdPrefix}__item-${index}`"
            @click="handleDownloadClick(file)"
            :class="['download-option-item mx-2 my-1 rounded', isDownloadAvailable(file.stage) ? 'cursor-pointer' : 'cursor-not-allowed']"
            :disabled="!isDownloadAvailable(file.stage)"
          >
            <v-list-item-icon class="mr-3 my-auto">
              <v-icon :data-testid="`${testIdPrefix}__item-icon-${index}`" :color="getDownloadStatusColor(file.stage)" small>{{ getDownloadStatusIcon(file.stage) }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title :data-testid="`${testIdPrefix}__item-title-${index}`" class="font-weight-medium text-truncate username">{{ file.userid_detail[0]?.name || file.userid_detail[0]?.contactemail  || "Excel File"}}( {{ file.userid_detail[0]?.contactemail }} )</v-list-item-title>
              <v-list-item-subtitle :class="[getDownloadStatusColor(file.stage) + '--text']">{{ getDownloadStatusText(file.stage) }} | {{ formatDateTime(file.created_at) }}</v-list-item-subtitle>
            </v-list-item-content>
            <!-- {{  file }} -->
            <v-list-item-icon class="ml-3 my-auto" v-if="isDownloadAvailable(file.stage)">
              <v-icon :data-testid="`${testIdPrefix}__download-icon-${index}`" :color="getDownloadStatusColor(file.stage)" small>mdi-download</v-icon>
            </v-list-item-icon>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import { TM_MESSAGES, DOWNLOAD_STAGES, isDownloadInProgress, isDownloadFailed, isDownloadReady } from '../../../../utils/constants';

export default {
  name: 'DownloadExcel',
  props: {
    /**
     * Main button text
     */
    buttonText: {
      type: String,
      default: 'Generate Excel'
    },
    /**
     * Button color (Vuetify color name)
     */
    buttonColor: {
      type: String,
      default: 'secondaryBtn'
    },
    /**
     * Array of download history files
     */
    downloadHistory: {
      type: Array,
      default: () => []
    },
    /**
     * Loading state for history
     */
    loadingHistory: {
      type: Boolean,
      default: false
    },
    /**
     * Prefix for data-testid attributes
     */
    testIdPrefix: {
      type: String,
      default: 'download-excel'
    },
    /**
     * Compact mode for V2 — reduces header height
     */
    compact: {
      type: Boolean,
      default: false
    },
    /**
     * Current user's ID. When provided, the Generate button is only
     * disabled when *this* user has a download in progress (stage 0 or 4).
     * Falls back to checking all users if not supplied.
     */
    currentUserId: {
      type: String,
      default: ''
    },
    /**
     * External disabled state. When true, the Generate button is disabled
     * regardless of active download status.
     */
    disabled: {
      type: Boolean,
      default: false
    },
    /**
     * Maximum record count allowed for export.
     * Displayed in the disabled tooltip when the limit is exceeded.
     */
    maxExportRecords: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      menuOpen: false,
      TM_MESSAGES,
      DOWNLOAD_STAGES
    };
  },
  computed: {
    /**
     * Check if the current user has an active download in progress.
     * When currentUserId is provided, only that user's in-progress downloads
     * disable the Generate button. Otherwise falls back to checking all entries.
     * Downloads with stage 0 or 4 that are older than 5 minutes are considered
     * stale (backend may have failed to pick them up) and will NOT disable the button.
     * @returns {Boolean} True if the current user has a non-stale download with stage 0 or 4
     */
    hasActiveDownload() {
      const STALE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes
      const now = Date.now();

      const isActiveAndNotStale = file => {
        if (!isDownloadInProgress(file.stage)) return false;
        const createdAt = new Date(file.created_at).getTime();
        return (now - createdAt) < STALE_THRESHOLD_MS;
      };

      if (this.currentUserId) {
        return this.downloadHistory.some(
          file => file.userid === this.currentUserId && isActiveAndNotStale(file)
        );
      }
      return this.downloadHistory.some(isActiveAndNotStale);
    },

    /**
     * Combined disabled state: external disabled prop OR active download in progress.
     * @returns {Boolean}
     */
    isGenerateDisabled() {
      return this.disabled || this.hasActiveDownload;
    },

    /**
     * Tooltip text when the Generate button is disabled.
     * Shows a record-limit message when externally disabled, otherwise the in-progress message.
     * @returns {String}
     */
    disabledTooltipText() {
      if (this.disabled && this.maxExportRecords > 0) {
        return TM_MESSAGES.EXPORT_LIMIT_EXCEEDED(this.maxExportRecords);
      }
      if (this.disabled) return TM_MESSAGES.EXPORT_LIMIT_EXCEEDED_GENERIC;
      return TM_MESSAGES.DOWNLOAD_IN_PROGRESS;
    }
  },
  methods: {
    /**
     * Format date/time using $nova helper
     * @param {String|Date} dt - Date to format
     * @returns {String} Formatted date string
     */
    formatDateTime(dt) {
      return this.$nova ? this.$nova.formatDateTime(dt) : dt;
    },
    /**
     * Get the icon for download status based on stage
     * @param {Number} stage - File processing stage
     * @returns {String} MDI icon name
     */
    getDownloadStatusIcon(stage) {
      if (isDownloadInProgress(stage)) {
        return 'mdi-progress-clock'; // In progress
      } else if (isDownloadFailed(stage)) {
        return 'mdi-alert-circle'; // Failed
      }
      return 'mdi-check-circle'; // Downloaded/Ready
    },
    /**
     * Get the color for download status based on stage
     * @param {Number} stage - File processing stage
     * @returns {String} Vuetify color name
     */
    getDownloadStatusColor(stage) {
      if (isDownloadInProgress(stage)) {
        return 'info'; // In progress
      } else if (isDownloadFailed(stage)) {
        return 'error'; // Failed
      }
      return 'success'; // Downloaded/Ready
    },
    /**
     * Get status text for download based on stage
     * @param {Number} stage - File processing stage
     * @returns {String} Status text
     */
    getDownloadStatusText(stage) {
      if (isDownloadInProgress(stage)) {
        return 'Generating...';
      } else if (isDownloadFailed(stage)) {
        return 'Failed';
      }
      return 'Ready to download';
    },
    /**
     * Check if download is available based on stage
     * @param {Number} stage - File processing stage
     * @returns {Boolean} Whether file can be downloaded
     */
    isDownloadAvailable(stage) {
      return isDownloadReady(stage);
    },
    /**
     * Handle download history file click with status check
     * @param {Object} file - File object
     */
    handleDownloadClick(file) {
      if (this.isDownloadAvailable(file.stage)) {
        this.$emit('download', file);
        this.menuOpen = false;
      } else if (isDownloadFailed(file.stage)) {
        this.$store.commit('sbError', 'Download failed. Please try again.');
      } else {
        this.$store.commit('sbInfo', 'File is still processing. Please wait.');
      }
    },
    /**
     * Handle refresh button click.
     * Uses @click.stop to prevent v-menu from intercepting the event.
     */
    handleRefresh() {
      this.$emit('refresh');
    }
  },
  beforeDestroy() {
    this.menuOpen = false;
  }
};
</script>

<style lang="scss" scoped>
.download-menu-card {
  width: 400px !important;
  min-width: 400px !important;
  max-width: 400px !important;
}

.download-option-item {
  &:hover:not([disabled]) {
    background-color: var(--color-surface-hover, rgba(0, 0, 0, 0.04));
  }
}

.cursor-pointer {
  cursor: pointer;
}

.cursor-not-allowed {
  cursor: not-allowed;
  opacity: 0.6;
}
.username{
  font-size: var(--font-size-xs) !important;
}

.download-excel--compact ::v-deep .v-btn {
  background-color: var(--color-surface-default) !important;
  color: var(--color-text-secondary) !important;
  border: 1px solid var(--color-border-alt) !important;
  font-size: var(--font-size-xs);
  text-transform: none;
  letter-spacing: normal;
  box-shadow: none !important;
  height: 2.5rem;
  min-height: 2.5rem;
  max-height: 2.5rem;

  &::before {
    background-color: transparent;
  }

  &:hover {
    background-color: var(--color-surface-muted) !important;
  }

  .v-icon {
    color: var(--color-text-secondary) !important;
  }
}

.download-excel--compact ::v-deep .rounded-r-0 {
  border-right-color: var(--color-border-alt) !important;
  border-radius: 0.375rem 0 0 0.375rem !important;
}

.download-excel--compact ::v-deep .rounded-l-0 {
  border-left: none !important;
  border-radius: 0 0.375rem 0.375rem 0 !important;
}
</style>
